import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Navigate, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  AppBar, Toolbar
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolIcon from "@mui/icons-material/School";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import SubjectIcon from "@mui/icons-material/Subject";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const AssignmentUpdate = ({ assignmentId }) => {
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [searchParams] = useSearchParams();
  const branchIdFromQuery = searchParams.get("Branch_Id");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  let navigate = useNavigate();
  const [teachersList, setTeacherList] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [assignmentDetails, setAssignmentDetails] = useState({
    assignmentDate: "",
    submissionDate: "",
    assignmentDescription: "",
    assignmentFilePath: "",
    assignmentFile: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Info dialog state
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setIsLoading(true);

        // Get assignment ID from URL
        const assignmentIdFromUrl = window.location.pathname.split("/")[2];
        const token = sessionStorage.getItem("token");
        const url = process.env.REACT_APP_BASE_URL;
        const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

        // Fetch teachers first
        const teachersData = await fetchTeachersData();
        setTeacherList(teachersData);

        // Fetch assignment details
        const assignmentResponse = await fetch(
          `${url}/AssignmentCreate/GetAssignmentsById?Branch_Id=${branchId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ assignmentId: assignmentIdFromUrl }),
          }
        );

        if (assignmentResponse.ok) {
          const response = await assignmentResponse.json();

          // ✅ Extract the assignment data from the response
          const assignmentData = response.data && response.data[0];

          if (!assignmentData) {
            console.error("No assignment data found");
            return;
          }

          console.log("Assignment Data:", assignmentData); // Debug log

          // Convert dates from DD/MM/YYYY to YYYY-MM-DD format for date inputs
          const convertDateToInputFormat = (dateStr) => {
            if (!dateStr) return "";
            const parts = dateStr.split("/");
            if (parts.length !== 3) return "";
            const [day, month, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          };

          // Set assignment details
          setAssignmentDetails({
            assignmentDate: convertDateToInputFormat(assignmentData.assignmentDate),
            submissionDate: convertDateToInputFormat(assignmentData.submissionDate),
            assignmentDescription: assignmentData.assignmentDescription || "",
            assignmentFilePath: assignmentData.assignmentFile || "",
            assignmentFile: null,
          });

          console.log("Setting teacher:", assignmentData.teacherId); // Debug log

          // Set teacher and fetch dependent data
          if (assignmentData.teacherId) {
            setSelectedTeacher(assignmentData.teacherId);

            // Fetch classes for this teacher
            const classesData = await fetchClassesData(assignmentData.teacherId);
            setClassData(classesData);

            console.log("Classes loaded:", classesData); // Debug log

            // Set class and fetch sections
            if (assignmentData.classId) {
              setSelectedClass(assignmentData.classId);

              const sectionsData = await fetchSectionsData(
                assignmentData.teacherId,
                assignmentData.classId
              );
              setSectionData(sectionsData);

              console.log("Sections loaded:", sectionsData); // Debug log

              // Set section and fetch subjects
              if (assignmentData.sectionId) {
                setSelectedSection(assignmentData.sectionId);

                const subjectsData = await fetchSubjectsData(
                  assignmentData.classId,
                  assignmentData.sectionId,
                  assignmentData.teacherId
                );
                setSubjectData(subjectsData);

                console.log("Subjects loaded:", subjectsData); // Debug log

                // Set subject
                if (assignmentData.subjectId) {
                  setSelectedSubject(assignmentData.subjectId);
                }
              }
            }
          }
        } else {
          console.error("Failed to fetch assignment details");
        }
      } catch (error) {
        console.error("Error in initializeComponent:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, [branchIdFromQuery]);

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    // Set the branchId from query parameter or sessionStorage
    const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);

    if (isMasterUser) {
      fetchBranches();
    }
  }, [branchIdFromQuery]);

  // Separate functions that return data directly
  const fetchTeachersData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

      const response = await fetch(`${apiUrl}/AssignmentCreate/GetTeacher?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching teachers:", error);
      return [];
    }
  };

  const fetchClassesData = async (teacherId) => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

      const response = await fetch(`${url}/AssignmentCreate/GetClass?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ teacherId }),
      });

      const data = await response.json();

      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching classes:", error);
      return [];
    }
  };

  const fetchSectionsData = async (teacherId, classId) => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

      const response = await fetch(`${url}/AssignmentCreate/ddlSection_clsId?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ teacherId, classId }),  // ✅ Fixed
      });

      const data = await response.json();

      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching sections:", error);
      return [];
    }
  };

  const fetchSubjectsData = async (classId, sectionId, teacherId) => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

      const response = await fetch(`${url}/AssignmentCreate/Subjectddl?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ classId, sectionId, teacherId }),
      });

      const data = await response.json();

      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching subjects:", error);
      return [];
    }
  };

  // Event handlers for dropdown changes
  const handleTeacherChange = async (event) => {
    const selectedTeacherId = event.target.value;
    setSelectedTeacher(selectedTeacherId);

    // Reset dependent selections
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setSectionData([]);
    setSubjectData([]);

    // Fetch new classes
    const classesData = await fetchClassesData(selectedTeacherId);
    setClassData(classesData);
  };

  const handleClassChange = async (event) => {
    const selectedClassId = event.target.value;
    setSelectedClass(selectedClassId);

    // Reset dependent selections
    setSelectedSection("");
    setSelectedSubject("");
    setSubjectData([]);

    // Fetch new sections
    const sectionsData = await fetchSectionsData(selectedTeacher, selectedClassId);
    setSectionData(sectionsData);
  };

  const handleSectionChange = async (event) => {
    const selectedSectionId = event.target.value;
    setSelectedSection(selectedSectionId);

    // Reset subject
    setSelectedSubject("");

    // Fetch new subjects
    const subjectsData = await fetchSubjectsData(selectedClass, selectedSectionId, selectedTeacher);
    setSubjectData(subjectsData);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    const formatDate = (dateStr) => {
      const [year, month, day] = dateStr.split("-");
      return `${day}/${month}/${year}`;
    };

    const formattedAssignmentDate = formatDate(assignmentDetails.assignmentDate);
    const formattedSubmissionDate = formatDate(assignmentDetails.submissionDate);
    const formData = new FormData();

    formData.append('assignmentId', assignmentId);
    formData.append('ClassId', selectedClass);
    formData.append('SectionId', selectedSection);
    formData.append('SubjectId', selectedSubject);
    formData.append('TeacherId', selectedTeacher);
    formData.append('AssignmentDescription', assignmentDetails.assignmentDescription);
    formData.append('AssignmentDate', formattedAssignmentDate);
    formData.append('AssignmentSubmitDate', formattedSubmissionDate);

    if (assignmentDetails.assignmentFile) {
      formData.append('AssignMentFile', assignmentDetails.assignmentFile);
    }

    try {
      const token = sessionStorage.getItem('token');
      const url = process.env.REACT_APP_BASE_URL;
      const branchId = branchIdFromQuery || sessionStorage.getItem("branchId");

      const apiUrl = `${url}/AssignmentCreate/Id?Branch_Id=${branchId}`;
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        console.log('Assignment updated successfully');
        setShowSuccessModal(true);
      } else {
        console.error('Failed to update assignment');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const handleFileChange = (e) => {
    setAssignmentDetails({
      ...assignmentDetails,
      assignmentFile: e.target.files[0],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleUpdate(e);
  };

  if (isLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>Loading assignment details...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#0B1F3D",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>

          {/* Left: Title + Info Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#ffffff",
              }}
            >
              Update Assignment
            </Typography>

            {/* ── Info Button ── */}
            <Tooltip title="About this form" arrow>
              <IconButton
                onClick={() => setInfoOpen(true)}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  border: "1.5px solid rgba(255,255,255,0.35)",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  ml: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor: "rgba(100,181,246,0.2)",
                    borderColor: "#64B5F6",
                    transform: "scale(1.1)",
                    boxShadow: "0 0 8px rgba(100,181,246,0.4)",
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right: Branch Dropdown */}
          {isMaster && (
            <Box sx={{ position: "relative" }}>
              <FormControl
                size="small"
                disabled
                sx={{
                  width: 220,
                  "& .MuiInputBase-root.Mui-disabled": {
                    color: "#ffffff !important",
                    WebkitTextFillColor: "#ffffff !important",
                  }
                }}
              >
                <InputLabel
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    top: "-2px",
                    "&.Mui-focused": { color: "#64B5F6" },
                    "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ""}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  label="Branch"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: "#ffffff",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "0.3px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                    "& .MuiSelect-select.Mui-disabled": {
                      color: "#ffffff !important",
                      WebkitTextFillColor: "#ffffff !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                      borderWidth: "1px",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.11)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64B5F6",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255,255,255,0.5)",
                      transition: "color 0.2s ease",
                    },
                    "&.Mui-focused .MuiSelect-icon": {
                      color: "#64B5F6",
                    },
                    "& .MuiSelect-select": {
                      py: 1.1,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        border: "1px solid rgba(11,31,61,0.12)",
                        boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        "& .MuiList-root": { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#0B1F3D",
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: "6px",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: "rgba(11,31,61,0.07)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25,118,210,0.1)",
                          color: "#1565C0",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "rgba(25,118,210,0.15)",
                          },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: "20px", marginTop: 6 }}>
        <form style={{ marginTop: "10px" }} onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <InputLabel htmlFor="teacher">Teacher</InputLabel>
                <Select
                  id="teacher"
                  value={selectedTeacher}
                  onChange={handleTeacherChange}
                  label="Teacher"
                >
                  {teachersList.map((item) => (
                    <MenuItem key={item.employeeId} value={item.employeeId}>
                      {item.employeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <InputLabel htmlFor="class">Class</InputLabel>
                <Select
                  id="class"
                  value={selectedClass}
                  onChange={handleClassChange}
                  label="Class"
                >
                  {classData.map((classItem) => (
                    <MenuItem key={classItem.classId} value={classItem.classId}>
                      {classItem.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <InputLabel htmlFor="section">Section</InputLabel>
                <Select
                  id="section"
                  value={selectedSection}
                  onChange={handleSectionChange}
                  label="Section"
                >
                  {sectionData.map((sectionItem) => (
                    <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                      {sectionItem.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <InputLabel htmlFor="subject">Subject</InputLabel>
                <Select
                  id="subject"
                  value={selectedSubject}
                  onChange={(event) => setSelectedSubject(event.target.value)}
                  label="Subject"
                >
                  {subjectData.map((subjectItem) => (
                    <MenuItem key={subjectItem.subjectId} value={subjectItem.subjectId}>
                      {subjectItem.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Assignment Date"
                type="date"
                fullWidth
                value={assignmentDetails.assignmentDate}
                onChange={(e) =>
                  setAssignmentDetails({
                    ...assignmentDetails,
                    assignmentDate: e.target.value,
                  })
                }
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                label="Submission Date"
                type="date"
                fullWidth
                value={assignmentDetails.submissionDate}
                onChange={(e) =>
                  setAssignmentDetails({
                    ...assignmentDetails,
                    submissionDate: e.target.value,
                  })
                }
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                id="assignmentDescription"
                label="Assignment Description"
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                style={{ marginBottom: "20px" }}
                value={assignmentDetails.assignmentDescription}
                onChange={(e) =>
                  setAssignmentDetails({
                    ...assignmentDetails,
                    assignmentDescription: e.target.value,
                  })
                }
              />

              <input
                type="file"
                onChange={handleFileChange}
                style={{ marginBottom: "20px" }}
              />
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Grid container justifyContent="end" alignItems="center">
        <Button
          variant="contained"
          onClick={handleUpdate}
          color="primary"
          style={{
            width: "90px",
            height: "50px",
            padding: "10px 0",
            fontWeight: 'bold',
            marginTop: 16,
            marginRight: 7
          }}
        >
          SAVE
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          color="error"
          style={{
            width: "90px",
            height: "50px",
            padding: "10px 0",
            fontWeight: 'bold',
            marginTop: 16
          }}
        >
          CANCEL
        </Button>
      </Grid>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Assignment Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Assignment has been updated successfully.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            boxShadow: "0 12px 40px rgba(11,31,61,0.2)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0B1F3D 0%, #1565C0 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 22 }} />
            Update Assignment — Form Guide
          </Box>
        
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>

          {/* Purpose */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            Use this form to update an existing assignment. All fields are pre-filled with
            the current assignment data. Make your changes and click Save to apply the update.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Fields */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Field Reference
          </Typography>

          <List dense disablePadding>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <PersonOutlineIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Teacher</Typography>}
                secondary="Pre-filled with the existing teacher. Changing it resets and reloads Class, Section, and Subject."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SchoolIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Class</Typography>}
                secondary="Pre-filled with the existing class. Changing it resets and reloads Section and Subject."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <MeetingRoomOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Section</Typography>}
                secondary="Pre-filled with the existing section. Changing it resets and reloads Subject."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SubjectIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Subject</Typography>}
                secondary="Pre-filled with the existing subject. Loaded based on the selected Teacher + Class + Section combination."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <CalendarTodayIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Assignment Date</Typography>}
                secondary="Pre-filled with the original assignment date. Can be changed as needed."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <EventAvailableIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Submission Date</Typography>}
                secondary="Pre-filled with the original submission deadline. Can be updated independently."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <ArticleOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Assignment Description</Typography>}
                secondary="Pre-filled with the existing description. Edit to update the assignment instructions or details."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <UploadFileOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>File Upload</Typography>}
                secondary="Optional. Upload a new file to replace the existing attachment. If no new file is selected, the original file is kept."
              />
            </ListItem>

            {isMaster && (
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
                <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                  <LocationOnIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={600}>Branch (Master Only — Read Only)</Typography>}
                  secondary="Displays the branch this assignment belongs to. It is pre-set from the URL and cannot be changed on this form."
                />
              </ListItem>
            )}

          </List>

          <Divider sx={{ my: 2 }} />

          {/* Buttons */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Buttons
          </Typography>
          <List dense disablePadding>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SaveOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Save</Typography>}
                secondary="Submits the updated assignment. A success modal confirms the update; clicking OK navigates back."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <ArrowBackIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Cancel</Typography>}
                secondary="Navigates back to the previous page immediately without saving any changes."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Important notes */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Important Notes
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {[
              "All fields are pre-filled from the existing assignment record on page load.",
              "Teacher → Class → Section → Subject is a cascading chain; changing any level resets and reloads the ones below it.",
              "Dates are stored in dd/mm/yyyy format internally but displayed as yyyy-mm-dd in the form.",
              "If no new file is uploaded, the existing file attachment is preserved.",
              "The Branch field (master users) is read-only and sourced from the URL query parameter.",
              "Cancel navigates back immediately without any confirmation prompt.",
            ].map((note, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                {note}
              </Typography>
            ))}
          </Box>

        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#f5f5f5", px: 3, py: 2 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#0B1F3D",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#1565C0" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── End Info Dialog ── */}

    </Container>
  );
};

export default AssignmentUpdate;